import { useCallback, useEffect, useState } from "react";
import type { DocumentType, UploadResponse } from "./api";
import { listTypes, me, uploadPdf } from "./api";
import { ConfigDropdown } from "./components/ConfigDropdown";
import { ConfigModal } from "./components/ConfigModal";
import { UploadProgress } from "./components/UploadProgress";
import { UploadZone } from "./components/UploadZone";
import { VerdictPanel } from "./components/VerdictPanel";

// Snelle page-count via regex op PDF-bytes. Vermijdt pdfjs-worker-setup +
// CDN-afhankelijkheid; werkt voor onversleutelde PDFs (alle factuur-PDFs die
// Forms accepteert). Faalt gracefully met return 0 → UploadProgress toont
// generieke "Document voorbereiden..." tekst zonder page-N-van-M.
async function getPdfPageCount(file: File): Promise<number> {
  try {
    const buf = await file.arrayBuffer();
    const txt = new TextDecoder("latin1").decode(buf);
    // Catalog bevat /Type /Pages ... /Count N — pak de laatste (= root)
    const matches = [...txt.matchAll(/\/Type\s*\/Pages[\s\S]*?\/Count\s+(\d+)/g)];
    if (matches.length > 0) return parseInt(matches[matches.length - 1][1], 10);
    // Fallback: tel /Type /Page markers (zonder trailing s)
    const page = txt.match(/\/Type\s*\/Page(?![a-zA-Z])/g);
    return page ? page.length : 0;
  } catch {
    return 0;
  }
}

type User = { email: string; name: string } | null;

export default function App() {
  const [user, setUser] = useState<User>(null);
  const [types, setTypes] = useState<DocumentType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pageCount, setPageCount] = useState(0);  // voor UploadProgress-schatting
  const [error, setError] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  // Bewaar laatste File om "Opnieuw met OCR" re-upload mogelijk te maken.
  const [lastFile, setLastFile] = useState<File | null>(null);

  const refreshTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      setTypes(await listTypes());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    me().then((d) => {
      if (!d.user) {
        // Auto-login via Keycloak zodra er geen sessie is (consistent met andere apps).
        window.location.href = "/auth/login";
        return;
      }
      setUser(d.user);
    });
    refreshTypes();
  }, [refreshTypes]);

  const handleFile = useCallback(
    async (file: File, forceOcr: boolean = false) => {
      setUploading(true);
      setError(null);
      setResult(null);
      setFilename(file.name);
      setLastFile(file);
      setPageCount(0);
      // Lees page count parallel aan de upload (niet await'en vóór upload).
      // getPdfPageCount is read-only op bytes; upload start direct.
      getPdfPageCount(file).then(setPageCount).catch(() => {});
      try {
        const res = await uploadPdf(file, forceOcr);
        setResult(res);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setUploading(false);
      }
    },
    []
  );

  const handleRetryOcr = useCallback(() => {
    if (lastFile) handleFile(lastFile, true);
  }, [lastFile, handleFile]);

  const handleTypeCreated = useCallback(async () => {
    await refreshTypes();
    setResult(null);
  }, [refreshTypes]);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col">
      <header className="border-b bg-white px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <a href="https://app.tensig.nl/" title="Terug naar Tensig-portal" className="flex items-center">
            <img src="/logo.png" alt="Tensig" className="h-7 w-auto" />
          </a>
          <div>
            <h1 className="text-xl font-semibold">Tensig Forms</h1>
            <p className="text-xs text-gray-500">Semi-gestructureerde documenten trainer · fase 2</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <a
            href="/help/index.html"
            target="_blank"
            rel="noopener"
            className="text-sm text-gray-600 hover:text-blue-700 inline-flex items-center gap-1"
            title="Open de helpgids in een nieuw tabblad"
          >
            Help
          </a>
          <ConfigDropdown
            types={types}
            loading={typesLoading}
            onChanged={refreshTypes}
            onOpenManager={() => setConfigOpen(true)}
          />
          {user ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-gray-600">{user.email}</span>
              <a
                href="/auth/logout"
                title="Uitloggen"
                className="inline-flex items-center justify-center px-2 py-1 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </a>
            </div>
          ) : (
            <a
              href="/auth/login"
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
            >
              Inloggen via Keycloak
            </a>
          )}
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <main className="flex-1 px-6 py-6 overflow-y-auto">
          <div className={`${result ? "w-full" : "max-w-3xl"} mx-auto space-y-6`}>
            {!result && !uploading && <UploadZone onFile={handleFile} />}
            {uploading && <UploadProgress pageCount={pageCount} />}
            {error && (
              <div className="border border-red-300 bg-red-50 rounded p-4 text-sm text-red-900">
                <strong>Fout:</strong> {error}
              </div>
            )}
            {result && !uploading && (
              <>
                <button
                  onClick={() => setResult(null)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  &larr; Nieuwe upload
                </button>
                <VerdictPanel
                  result={result}
                  filename={filename ?? undefined}
                  onTypeCreated={handleTypeCreated}
                  onRetryOcr={lastFile ? handleRetryOcr : undefined}
                />
              </>
            )}
          </div>
        </main>
      </div>

      <ConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        types={types}
        typesLoading={typesLoading}
        onTypesChanged={refreshTypes}
      />
    </div>
  );
}
